import { useEffect, useState, type CSSProperties } from "react";
import { Trans } from "react-i18next";

const CAROUSEL_ITEM_HEIGHT = 20;
const DEFAULT_CAROUSEL_DURATION = 5000;
const CAROUSEL_TRANSITION_DURATION = 500;

interface TextCarouselProps {
  texts: string[];
  duration?: number;
}

export const TextCarousel = ({ texts, duration = DEFAULT_CAROUSEL_DURATION }: TextCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);

  useEffect(() => {
    if (texts.length <= 1) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setIsTransitioning(true);
      setActiveIndex((currentIndex) => currentIndex + 1);
    }, duration);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [duration, texts.length]);

  useEffect(() => {
    if (texts.length <= 1 || activeIndex !== texts.length) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsTransitioning(false);
      setActiveIndex(0);
    }, CAROUSEL_TRANSITION_DURATION);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeIndex, texts.length]);

  if (!texts.length) {
    return null;
  }

  if (texts.length === 1) {
    return (
      <div className="h-[20px] overflow-hidden text-center leading-[20px]">
        <Trans>{texts[0]}</Trans>
      </div>
    );
  }

  const carouselTexts = [...texts, texts[0]];
  const carouselStyle: CSSProperties = {
    transform: `translateY(-${activeIndex * CAROUSEL_ITEM_HEIGHT}px)`,
    transitionDuration: isTransitioning ? `${CAROUSEL_TRANSITION_DURATION}ms` : "0ms",
  };

  return (
    <div className="h-[20px] overflow-hidden text-center leading-[20px]">
      <div
        className="flex flex-col transition-transform ease-[cubic-bezier(0.65,0,0.35,1)] will-change-transform"
        style={carouselStyle}
      >
        {carouselTexts.map((text, index) => (
          <div className="h-[20px] whitespace-nowrap" key={`${text}-${index}`}>
            <Trans>{text}</Trans>
          </div>
        ))}
      </div>
    </div>
  );
};
